import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import Lozenge from '@atlaskit/lozenge';
import { isBugBountyParticipant } from '@atlassian/amkt-common';
import { useStatsig } from '@atlassian/mpac-feature-flags';
import { Ratings } from '@atlassian/mpac-primitives';
import { CloudFortifiedStatusValues } from '@atlassian/mpac-types';
import { AtlassianProductHostingType } from '@atlassian/mpac-types/graphql-gateway-types';
import { AppTileHighlightedText } from '@atlassian/mpac-ui';
import { DateUtils, useHostUrl, useMountPath } from '@atlassian/mpac-utils';
import { getBootstrapLinks } from '../../Config';
import { TEST_IDS } from '../../primitives/testIds';
import { createTileHighlights } from '../../utils/createTileHighlights';
import { CertificationBadge } from '../CertificationBadge/CertificationBadge';
import { InstallDownloadMetrics } from '../InstallDownloadMetrics';
import { PartnerDetails } from '../PartnerDetails/PartnerDetails';
import { AppLogo } from './AppLogo';
import { AppLogoWrapper, AppNameWrapper, AppTileButton, AppTileContainer, AppTileHeader, AppTileMainContentWrapper, Heading, NoCertification, PartnerDetailsWrapper, RatingsAndInstallsContainer, Tagline } from './AppTile.styles';
import { BadgeVariant, Certification } from './AppTile.types';
import { getAppListingUrl } from './AppTile.utils';
import { AppTileBadge } from './AppTileBadge';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// eslint-disable-next-line max-lines-per-function
export function AppTile(_ref) {
  let {
    tileData,
    environment,
    source,
    hideTagline = false,
    hidePartnerDetails = false,
    onTileClick,
    tileRef,
    isAppInstalled
  } = _ref;
  const {
    enableRunsOnAtlassian,
    enableMediaFromProductListing
  } = useStatsig();
  const isRunsOnAtlassianEnabled = enableRunsOnAtlassian();
  const links = getBootstrapLinks(environment);
  const appLogoUrl = getAppLogoUrl();
  const appName = getAppName();
  const tagLine = getTagLine();
  const certification = getCertification();
  const tileBadgeVariant = getTileBadgeVariant();
  const hostUrl = useHostUrl();
  const mountPath = useMountPath();
  const {
    downloads,
    activeInstalls: installs,
    bundled
  } = tileData.distribution;
  const partner = tileData.vendor;
  const partnerDetailProps = getPartnerDetailProps();
  function getAppLogoUrl() {
    const imageId = tileData.logo.highRes || tileData.logo.image;
    if (enableMediaFromProductListing()) {
      return links.mediaFileProductListing.expand({
        imageId
      });
    }
    return links.mediaFile.expand({
      imageId
    });
  }
  function getAppName() {
    if (tileData._highlightResult) {
      const highlightedText = createTileHighlights(tileData._highlightResult, 'name', 'tokenizedName');
      return /*#__PURE__*/_jsx(AppTileHighlightedText, {
        highlightedText: highlightedText,
        originalText: tileData.name,
        overrideStyles: {
          color: "var(--ds-text)",
          fontWeight: 700
        }
      });
    } else {
      return tileData.name;
    }
  }
  function getTagLine() {
    if (tileData._highlightResult) {
      const highlightedText = createTileHighlights(tileData._highlightResult, 'tagLine', 'tokenizedTagLine');
      return /*#__PURE__*/_jsx(AppTileHighlightedText, {
        highlightedText: highlightedText,
        originalText: tileData.tagLine,
        overrideStyles: {
          color: "var(--ds-text)",
          fontWeight: 600
        }
      });
    } else {
      return tileData.tagLine;
    }
  }
  function getTileBadgeVariant() {
    var _tileData$tags;
    let badgeVariant;
    const marketingLabels = ((_tileData$tags = tileData.tags) === null || _tileData$tags === void 0 ? void 0 : _tileData$tags.marketingLabels) || [];
    if (marketingLabels.includes('Spotlight')) {
      badgeVariant = BadgeVariant.SPOTLIGHT;
    } else if (marketingLabels.includes('Bestseller')) {
      badgeVariant = BadgeVariant.BESTSELLER;
    } else if (marketingLabels.includes('Rising Star')) {
      badgeVariant = BadgeVariant.RISINGSTAR;
    } else {
      const releaseDate = tileData.releaseDate;
      if (releaseDate) {
        badgeVariant = Math.abs(DateUtils.differenceInDaysToNow(releaseDate)) < 14 ? BadgeVariant.NEW : undefined;
      }
    }
    return badgeVariant;
  }
  function getPartnerDetailProps() {
    var _partner$partnerTier;
    return {
      name: partner.name,
      partnerTier: (_partner$partnerTier = partner.partnerTier) === null || _partner$partnerTier === void 0 ? void 0 : _partner$partnerTier.type.toUpperCase()
    };
  }
  function getCertification() {
    var _tileData$programs, _tileData$programs$cl, _tileData$programs2, _tileData$programs3, _tileData$programs3$r;
    const isCloudFortifiedApp = ((_tileData$programs = tileData.programs) === null || _tileData$programs === void 0 ? void 0 : (_tileData$programs$cl = _tileData$programs.cloudFortified) === null || _tileData$programs$cl === void 0 ? void 0 : _tileData$programs$cl.status) === CloudFortifiedStatusValues.Approved;
    const isCloudSecurityParticipantApp = isBugBountyParticipant(AtlassianProductHostingType.Cloud, (_tileData$programs2 = tileData.programs) === null || _tileData$programs2 === void 0 ? void 0 : _tileData$programs2.bugBountyParticipant) && tileData.deployments.includes('cloud');
    const isRunsOnAtlassianApp = isRunsOnAtlassianEnabled && ((_tileData$programs3 = tileData.programs) === null || _tileData$programs3 === void 0 ? void 0 : (_tileData$programs3$r = _tileData$programs3.runsOnAtlassian) === null || _tileData$programs3$r === void 0 ? void 0 : _tileData$programs3$r.status);
    let certificationType;
    if (isRunsOnAtlassianApp) {
      certificationType = Certification.RUNS_ON_ATLASSIAN;
    } else if (isCloudFortifiedApp) {
      certificationType = Certification.CLOUD_FORTIFIED;
    } else if (isCloudSecurityParticipantApp) {
      certificationType = Certification.CLOUD_SECURITY_PARTICIPANT;
    }
    return certificationType;
  }
  function onTileClickHandler(e) {
    e.preventDefault();
    onTileClick === null || onTileClick === void 0 ? void 0 : onTileClick(tileData);
  }
  return /*#__PURE__*/_jsx(AppTileContainer, {
    children: /*#__PURE__*/_jsxs(AppTileButton, {
      href: getAppListingUrl(source, hostUrl, tileData, mountPath),
      onClick: onTileClickHandler,
      "data-testid": TEST_IDS.APP_TILE,
      ref: tileRef,
      target: "_blank",
      children: [tileBadgeVariant && /*#__PURE__*/_jsx(AppTileBadge, {
        variant: tileBadgeVariant
      }), /*#__PURE__*/_jsxs(AppTileMainContentWrapper, {
        children: [/*#__PURE__*/_jsxs(AppTileHeader, {
          children: [/*#__PURE__*/_jsx(AppLogoWrapper, {
            children: /*#__PURE__*/_jsx(AppLogo, {
              alt: tileData.name,
              loading: "lazy",
              src: appLogoUrl,
              "aria-hidden": "true"
            })
          }), /*#__PURE__*/_jsx(AppNameWrapper, {
            children: /*#__PURE__*/_jsx(Heading, {
              children: appName
            })
          })]
        }), !hidePartnerDetails && /*#__PURE__*/_jsx(PartnerDetailsWrapper, {
          children: /*#__PURE__*/_jsx(PartnerDetails, _objectSpread({}, partnerDetailProps))
        }), !hideTagline && /*#__PURE__*/_jsx(Tagline, {
          children: tagLine
        })]
      }), /*#__PURE__*/_jsxs("div", {
        children: [/*#__PURE__*/_jsxs(RatingsAndInstallsContainer, {
          children: [/*#__PURE__*/_jsx(Ratings, {
            testId: TEST_IDS.RATINGS,
            ratings: tileData.ratings
          }), isAppInstalled !== null && isAppInstalled !== void 0 && isAppInstalled(tileData.addonKey) ? /*#__PURE__*/_jsx(Lozenge, {
            isBold: true,
            children: "Added"
          }) : /*#__PURE__*/_jsx(InstallDownloadMetrics, {
            appName: tileData.name,
            installs: installs,
            downloads: downloads,
            isPreinstalled: bundled
          })]
        }), certification ? /*#__PURE__*/_jsx(CertificationBadge, {
          certification: certification
        }) : /*#__PURE__*/_jsx(NoCertification, {})]
      })]
    })
  });
}